import { useEffect } from "react";

import clsx from "clsx";
import { AnimatePresence } from "framer-motion";

import { usePageTitle } from "@/atoms/page-title-atom";
import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { ClicksAndViewsEnhancedModellingBanner } from "@features/clicks-and-view-enhanced-modelling/clicks-and-views-enhanced-modelling";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { OverviewProvider } from "@features/overview-v2";
import {
  AddTileModal,
  FiltersBar,
  MinimizedTopBar,
  OverviewEducationModal,
  OverviewGridView,
  TopBar,
} from "@features/overview-v2/components";
import { TopBar as TopBarV2 } from "@features/overview-v2/components/dashboard-v2";
import { useOverviewContext } from "@features/overview-v2/hooks";
import {
  useFeatureFlags,
  useNorthbeamQuery,
  useUpdateUserFeatureFlag,
} from "@hooks/http";
import { useDisclosure } from "@hooks/use-disclosure";
import { useIntersectionObserver } from "@hooks/use-intersection-observer";
import { prettyLogEvent } from "@internal/analytics";
import { type FetchSettingsDetails } from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";

export const OverviewV2 = () => {
  return (
    <CustomMetricsProvider>
      <OverviewProvider>
        <OverviewV2Content />
      </OverviewProvider>
    </CustomMetricsProvider>
  );
};

export const OverviewV2Content = () => {
  const [, setPageTitle] = usePageTitle();
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const { data } = useNorthbeamQuery<FetchSettingsDetails>(
    FETCH_SETTINGS_DETAILS,
    {},
  );
  const [updateUserFeatureFlag] = useUpdateUserFeatureFlag();

  const [
    educationModalOpened,
    { open: openEducationModal, close: closeEducationModal },
  ] = useDisclosure(false);

  useEffect(() => {
    const featureFlags = data?.me.featureFlags;
    if (
      featureFlags !== undefined &&
      featureFlags.showOverviewV2FirstTimeEducation === true
    ) {
      openEducationModal();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data]);

  useEffect(() => {
    setPageTitle("Overview");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.OVERVIEW,
      user,
    });
  }, [user]);

  const [
    addTileModalOpened,
    { open: openNewTileModal, close: closeNewTileModal },
  ] = useDisclosure(false);

  const [topLevelFiltersContainerElRef, isMinimizedTopBarVisible] =
    useIntersectionObserver();

  const [shadowControlElRef, isShadowVisible] = useIntersectionObserver();

  const { isMetadataLoading } = useOverviewContext();

  if (isMetadataLoading) {
    return <LoadingSlide />;
  }

  const headerGradient =
    "linear-gradient(180deg, var(--v2-bg-secondary, #F9FAFF) 83.66%, var(--v2-highlight-information-hover, #CDD3FF) 183.87%)";

  return (
    <>
      <div>
        {hasFeatureFlag("enableDashboardV2") ? (
          <>
            <div
              className="pt-5 px-6 flex items-center justify-between overflow-hidden"
              style={{ background: headerGradient }}
            >
              <div className="flex items-center gap-8">
                <div className="text-lg text-v2-primary font-bold flex items-center mb-2.5">
                  Home
                </div>
                <div className="flex items-center gap-6">
                  <div
                    className={clsx(
                      "text-base font-semibold h-11 px-1 pt-2 no-underline relative",
                      "text-v2-action group bg-v2-primary",
                      "shadow-[0px_0px_20px_0px_#0000001A]",
                    )}
                  >
                    <svg
                      className="absolute top-0 left-0.5 -translate-x-full group-hover:visible"
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="47"
                      viewBox="0 0 18 47"
                      fill="none"
                    >
                      <path
                        d="M18 47V0C12.4775 0 8 4.47754 8 10V38C8 42.418 4.41797 46 0 46H8V47H18Z"
                        fill="var(--v2-bg-primary)"
                      />
                    </svg>
                    Dashboard
                    <svg
                      className="absolute top-0 right-0.5 translate-x-full group-hover:visible"
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="47"
                      viewBox="0 0 18 47"
                      fill="none"
                    >
                      <path
                        d="M0 47H10V46H18C13.582 46 10 42.418 10 38V10C10 4.47754 5.52246 0 0 0V47Z"
                        fill="var(--v2-bg-primary)"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div ref={shadowControlElRef}></div>
            {(() => {
              const banner = <ClicksAndViewsEnhancedModellingBanner />;
              return (
                banner && (
                  <div className="px-2 pt-2 bg-v2-primary">
                    <div className="rounded-md overflow-hidden">{banner}</div>
                  </div>
                )
              );
            })()}
            <TopBarV2 isShadowVisible={isShadowVisible} />
          </>
        ) : (
          <>
            <AnimatePresence>
              {isMinimizedTopBarVisible && <MinimizedTopBar />}
            </AnimatePresence>
            <div ref={topLevelFiltersContainerElRef}>
              <TopBar />
              <FiltersBar onOpenNewTileModal={() => openNewTileModal()} />
            </div>
          </>
        )}
        <OverviewGridView className="mb-4 mt-1 ml-3 pl-8 pr-2" />
      </div>
      {addTileModalOpened && (
        <AddTileModal
          isOpen={addTileModalOpened}
          onClose={() => closeNewTileModal()}
        />
      )}
      <OverviewEducationModal
        isOpen={educationModalOpened}
        onClose={async () => {
          closeEducationModal();
          await updateUserFeatureFlag(
            "showOverviewV2FirstTimeEducation",
            false,
          );
        }}
      />
    </>
  );
};
